import { useQuery } from "@tanstack/react-query";
import { X } from "lucide-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { SidebarFooter } from "@/components/ui/sidebar";
import { useUser } from "@/lib/user-context";
import { cn } from "@/lib/utils";
import { trpc } from "@/utils/trpc";
import {
  type BannerVariant,
  resolveBannerConfig,
} from "./sidebar-trial-banner-config";

export { resolveBannerConfig } from "./sidebar-trial-banner-config";

// ---------------------------------------------------------------------------
// Dismiss helpers (Base upsell only)
// ---------------------------------------------------------------------------

const DISMISS_KEY = "stonecut:sidebar-base-upsell-dismissed";

function readDismissed(): boolean {
  try {
    return localStorage.getItem(DISMISS_KEY) === "1";
  } catch {
    return false;
  }
}

function writeDismissed() {
  try {
    localStorage.setItem(DISMISS_KEY, "1");
  } catch {
    // ignore — storage unavailable
  }
}

// ---------------------------------------------------------------------------
// Styling maps
// ---------------------------------------------------------------------------

const containerStyles: Record<BannerVariant, string> = {
  comfortable: "border-border/60 bg-muted/40 text-foreground/80",
  warning:
    "border-amber-300/70 bg-amber-50/70 text-amber-900 dark:border-amber-700/50 dark:bg-amber-950/30 dark:text-amber-200",
  urgent:
    "border-orange-400/70 bg-orange-50/70 text-orange-900 dark:border-orange-700/50 dark:bg-orange-950/30 dark:text-orange-200",
  expiring:
    "border-destructive/50 bg-destructive/5 text-destructive dark:bg-destructive/10",
  expired:
    "border-destructive/40 bg-destructive/5 text-destructive dark:bg-destructive/10",
  "limit-notice":
    "border-amber-300/70 bg-amber-50/70 text-amber-900 dark:border-amber-700/50 dark:bg-amber-950/30 dark:text-amber-200",
  "base-upsell": "border-border/60 bg-muted/40 text-foreground/80",
  canceling:
    "border-orange-400/70 bg-orange-50/70 text-orange-900 dark:border-orange-700/50 dark:bg-orange-950/30 dark:text-orange-200",
};

const iconStyles: Record<BannerVariant, string> = {
  comfortable: "text-muted-foreground",
  warning: "text-amber-600 dark:text-amber-400",
  urgent: "text-orange-600 dark:text-orange-400",
  expiring: "text-destructive",
  expired: "text-destructive",
  "limit-notice": "text-amber-600 dark:text-amber-400",
  "base-upsell": "text-primary",
  canceling: "text-orange-600 dark:text-orange-400",
};

const buttonVariantMap: Record<
  BannerVariant,
  "default" | "outline" | "destructive"
> = {
  comfortable: "outline",
  warning: "outline",
  urgent: "default",
  expiring: "default",
  expired: "default",
  "limit-notice": "default",
  "base-upsell": "outline",
  canceling: "default",
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function SidebarTrialBanner() {
  const { isManager } = useUser();
  const [dismissed, setDismissed] = useState(() => readDismissed());

  const overviewQuery = useQuery({
    ...trpc.getSubscriptionOverview.queryOptions(),
    enabled: isManager,
  });

  if (!isManager || overviewQuery.isLoading || !overviewQuery.data) {
    return null;
  }

  const { billingState, bypass, plan, limitNotice } = overviewQuery.data;
  const config = resolveBannerConfig({
    isTrial: billingState.isTrial,
    hasActivePaidSubscription: billingState.hasActivePaidSubscription,
    trialExpiresAt: billingState.trialExpiresAt,
    bypassActive: bypass?.active ?? false,
    planId: plan.id,
    limitNotice: limitNotice ?? null,
    baseUpsellDismissed: dismissed,
    cancelAtPeriodEnd: billingState.cancelAtPeriodEnd,
    currentPeriodEnd: billingState.currentPeriodEnd,
  });

  if (!config) {
    return null;
  }

  const { variant, Icon, heading, body, cta, ctaTo, dismissible } = config;

  function handleDismiss() {
    writeDismissed();
    setDismissed(true);
  }

  return (
    <SidebarFooter>
      <div
        className={cn(
          "rounded-lg border p-3 space-y-2.5",
          containerStyles[variant]
        )}
      >
        <div className="flex items-start gap-2">
          <Icon
            className={cn("mt-0.5 size-3.5 shrink-0", iconStyles[variant])}
          />
          <div className="min-w-0 flex-1 space-y-0.5">
            <p className="text-xs font-semibold leading-snug">{heading}</p>
            <p className="text-xs leading-relaxed opacity-80">{body}</p>
          </div>
          {dismissible && (
            <button
              type="button"
              onClick={handleDismiss}
              aria-label="Dismiss"
              className="shrink-0 rounded p-0.5 opacity-50 transition-opacity hover:opacity-100 focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/45 focus-visible:outline-none"
            >
              <X className="size-3" />
            </button>
          )}
        </div>
        <Button
          asChild
          size="xs"
          variant={buttonVariantMap[variant]}
          className="w-full"
        >
          <Link to={ctaTo ?? "/billing"}>{cta}</Link>
        </Button>
      </div>
    </SidebarFooter>
  );
}
